import { useMemo, useState } from "react";
import { Input, Button, cn } from "@porchpass/ui";
import { EyeOpenIcon, EyeClosedIcon } from "src/assets/icons";

interface ResetPasswordFormProps {
  onSubmit: (password: string) => void;
  onPasswordChange?: (password: string) => void;
  loading: boolean;
  invited?: boolean;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  onSubmit,
  onPasswordChange,
  loading,
  invited = false,
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmError, setConfirmError] = useState(false);

  const validatePassword = (pwd: string) => {
    return pwd.length >= 8;
  };

  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return { level: 0, text: "Start typing a password..." };
    const hasNumbers = /\d/.test(pwd);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    const hasLength = pwd.length >= 8;

    if (hasLength && hasNumbers && hasSpecialChars) {
      return { level: 3, text: "Strong password" };
    } else if (hasLength && (hasNumbers || hasSpecialChars)) {
      return { level: 2, text: "Decent password" };
    } else if (pwd.length > 0) {
      return { level: 1, text: "Weak password" };
    }
    return { level: 0, text: "Start typing a password..." };
  };

  const passwordStrength = getPasswordStrength(password);

  const passwordStrengthTextColor = useMemo(() => {
    return passwordStrength.level >= 3
      ? "text-green-700"
      : passwordStrength.level >= 2
        ? "text-yellow-700"
        : passwordStrength.level >= 1
          ? "text-red-700"
          : "text-[#687083]";
  }, [passwordStrength.level]);
  const passwordStrengthBackgroundColor = useMemo(() => {
    return passwordStrength.level >= 3
      ? "bg-green-700"
      : passwordStrength.level >= 2
        ? "bg-yellow-700"
        : passwordStrength.level >= 1
          ? "bg-red-700"
          : "bg-text-disabled";
  }, [passwordStrength.level]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    let hasError = false;

    if (!validatePassword(password)) {
      setPasswordError(true);
      hasError = true;
    }

    if (password !== confirmPassword) {
      setConfirmError(true);
      hasError = true;
    }

    if (hasError) return;

    onSubmit(password);
  };

  return (
    <form className="flex flex-col gap-4 w-full" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-3 w-full">
        <div className="flex gap-2 items-center">
          <div className="flex gap-0.5 items-end">
            <div
              className={`h-1 w-[3px] rounded-[1px] ${passwordStrengthBackgroundColor}`}
            />
            <div
              className={`w-[3px] h-[7px] rounded-[1px] ${passwordStrength.level >= 2 ? passwordStrengthBackgroundColor : "bg-text-disabled"}`}
            />
            <div
              className={`w-[3px] h-[10px] rounded-[1px] ${passwordStrength.level >= 3 ? passwordStrengthBackgroundColor : "bg-text-disabled"}`}
            />
          </div>
          <p
            className={cn(
              "font-normal text-[11px] leading-[14px] text-on-surface-tertiary",
              passwordStrengthTextColor,
            )}
          >
            {passwordStrength.text}
          </p>
        </div>

        <Input
          value={password}
          placeholder="Password"
          type={showPassword ? "text" : "password"}
          error={passwordError}
          helperText={
            passwordError ? "Password must be at least 8 characters" : undefined
          }
          onChange={(e) => {
            setPassword(e.target.value);
            setPasswordError(false);
            onPasswordChange?.(e.target.value);
          }}
          onBlur={() => {
            if (password && !validatePassword(password)) {
              setPasswordError(true);
            }
          }}
          size="s"
          rightSlot={
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
            </Button>
          }
        />
      </div>

      <Input
        value={confirmPassword}
        placeholder="Confirm Password"
        type={showPassword ? "text" : "password"}
        error={confirmError}
        helperText={confirmError ? "Passwords do not match" : undefined}
        onChange={(e) => {
          setConfirmPassword(e.target.value);
          setConfirmError(false);
        }}
        onBlur={() => {
          if (confirmPassword && password !== confirmPassword) {
            setConfirmError(true);
          }
        }}
        size="s"
      />

      <Button
        variant="primary"
        size="m"
        type="submit"
        loading={loading}
        disabled={!password || !confirmPassword}
        className="w-full"
      >
        {invited ? "Set password" : "Set new password"}
      </Button>
    </form>
  );
};
